<template>
  <div class="platform-resources-page">
    <div class="page-header">
      <div>
        <h1 class="page-title">平台资料</h1>
        <p class="page-subtitle">集中管理常用第三方资料链接，点击卡片即可打开</p>
      </div>
      <div class="header-actions">
        <button type="button" class="ghost-btn" @click="goBack">返回首页</button>
        <button type="button" class="primary-btn" @click="openAddModal">新增资料</button>
      </div>
    </div>

    <div v-if="statusMessage" class="status-banner" :class="statusType">
      {{ statusMessage }}
    </div>

    <div v-if="loading" class="placeholder-card">
      正在加载平台资料...
    </div>

    <div v-else-if="resources.length === 0" class="placeholder-card">
      还没有平台资料，点击右上角“新增资料”开始添加
    </div>

    <div v-else class="resource-grid">
      <div
        v-for="item in resources"
        :key="item.id"
        class="resource-card"
        @click="openResource(item)"
      >
        <button
          type="button"
          class="delete-btn"
          title="删除资料"
          @click.stop="deleteResource(item)"
        >
          删除
        </button>
        <div class="resource-index">资料 {{ item.sortIndex }}</div>
        <h2 class="resource-name">{{ item.resourceName }}</h2>
        <p class="resource-url">{{ item.resourceUrl }}</p>
        <div class="resource-footer">
          <span class="resource-tag">第三方链接</span>
          <span class="resource-open">点击打开</span>
        </div>
      </div>
    </div>

    <div v-if="showAddModal" class="modal-mask" @click.self="closeAddModal">
      <div class="modal-card">
        <div class="modal-header">
          <h3>新增平台资料</h3>
          <button type="button" class="modal-close" @click="closeAddModal">关闭</button>
        </div>

        <div class="modal-body">
          <label class="form-label" for="resource-name">资料名称</label>
          <input
            id="resource-name"
            v-model.trim="form.resourceName"
            type="text"
            class="form-input"
            maxlength="100"
            placeholder="例如：巨量课堂"
          />

          <label class="form-label" for="resource-url">第三方链接</label>
          <input
            id="resource-url"
            v-model.trim="form.resourceUrl"
            type="text"
            class="form-input"
            maxlength="1000"
            placeholder="请输入完整的 http:// 或 https:// 链接"
          />

          <p class="form-tip">保存后会写入数据库，当前卡密下的资料会在这里长期保留。</p>
        </div>

        <div class="modal-footer">
          <button type="button" class="ghost-btn" @click="closeAddModal">取消</button>
          <button type="button" class="primary-btn" :disabled="submitting" @click="submitResource">
            {{ submitting ? '保存中...' : '保存资料' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import API_CONFIG from '@/api/config'
import { createAdaptiveViewportMixin } from '@/mixins/adaptiveViewport'

export default {
  name: 'PlatformResources',
  mixins: [createAdaptiveViewportMixin({ designWidth: 1440, designHeight: 900, minScale: 0.6 })],
  data() {
    return {
      loading: false,
      submitting: false,
      showAddModal: false,
      statusMessage: '',
      statusType: 'success',
      resources: [],
      form: {
        resourceName: '',
        resourceUrl: ''
      }
    }
  },
  created() {
    this.fetchResources()
  },
  methods: {
    showStatus(message, type = 'success') {
      this.statusMessage = message
      this.statusType = type
    },
    async fetchResources() {
      this.loading = true
      this.statusMessage = ''
      try {
        const response = await axios.get(API_CONFIG.platformResourceList())
        if (Number(response?.data?.code) === 200) {
          const list = Array.isArray(response?.data?.data) ? response.data.data : []
          this.resources = list.map((item, index) => ({
            ...item,
            sortIndex: index + 1
          }))
          return
        }
        this.resources = []
        this.showStatus(response?.data?.msg || '获取平台资料失败', 'error')
      } catch (error) {
        this.resources = []
        this.showStatus(error?.response?.data?.msg || '获取平台资料失败，请稍后重试', 'error')
      } finally {
        this.loading = false
      }
    },
    openAddModal() {
      this.form = {
        resourceName: '',
        resourceUrl: ''
      }
      this.showAddModal = true
    },
    closeAddModal() {
      if (this.submitting) {
        return
      }
      this.showAddModal = false
    },
    async submitResource() {
      const resourceName = String(this.form.resourceName || '').trim()
      const resourceUrl = String(this.form.resourceUrl || '').trim()
      if (!resourceName) {
        this.showStatus('资料名称不能为空', 'error')
        return
      }
      if (!resourceUrl) {
        this.showStatus('第三方链接不能为空', 'error')
        return
      }
      if (!/^https?:\/\//i.test(resourceUrl)) {
        this.showStatus('第三方链接必须以 http:// 或 https:// 开头', 'error')
        return
      }

      this.submitting = true
      try {
        const response = await axios.post(API_CONFIG.platformResourceAdd(), {
          resourceName,
          resourceUrl
        })
        if (Number(response?.data?.code) === 200) {
          this.showAddModal = false
          this.showStatus('平台资料新增成功')
          await this.fetchResources()
          return
        }
        this.showStatus(response?.data?.msg || '平台资料新增失败', 'error')
      } catch (error) {
        this.showStatus(error?.response?.data?.msg || '平台资料新增失败，请稍后重试', 'error')
      } finally {
        this.submitting = false
      }
    },
    async deleteResource(item) {
      if (!item || !item.id) {
        return
      }
      if (!window.confirm(`确定删除资料“${item.resourceName}”吗？`)) {
        return
      }

      try {
        const response = await axios.delete(API_CONFIG.platformResourceDelete(item.id))
        if (Number(response?.data?.code) === 200) {
          this.showStatus('平台资料删除成功')
          await this.fetchResources()
          return
        }
        this.showStatus(response?.data?.msg || '平台资料删除失败', 'error')
      } catch (error) {
        this.showStatus(error?.response?.data?.msg || '平台资料删除失败，请稍后重试', 'error')
      }
    },
    openResource(item) {
      const url = String(item?.resourceUrl || '').trim()
      if (!url) {
        this.showStatus('当前资料链接为空，无法打开', 'error')
        return
      }
      window.open(url, '_blank')
    },
    goBack() {
      this.$router.push('/home')
    }
  }
}
</script>

<style scoped>
.platform-resources-page {
  min-height: 100vh;
  height: 100vh;
  padding: 18px 20px 20px;
  background: linear-gradient(135deg, #11254c 0%, #224a87 100%);
  color: #fff;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.page-header {
  max-width: 1200px;
  width: 100%;
  margin: 0 auto 14px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
}

.page-title {
  margin: 0 0 8px;
  font-size: 30px;
  font-weight: 700;
}

.page-subtitle {
  margin: 0;
  color: rgba(255, 255, 255, 0.78);
  font-size: 14px;
}

.header-actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.primary-btn,
.ghost-btn,
.delete-btn,
.modal-close {
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
}

.primary-btn,
.ghost-btn {
  min-width: 110px;
  padding: 10px 18px;
  border-radius: 10px;
  font-size: 14px;
}

.primary-btn {
  background: linear-gradient(45deg, #667eea, #7b61ff);
  color: #fff;
}

.primary-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.ghost-btn {
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.primary-btn:hover,
.ghost-btn:hover {
  transform: translateY(-1px);
}

.status-banner,
.placeholder-card {
  max-width: 1200px;
  width: 100%;
  margin: 0 auto 12px;
  border-radius: 16px;
  padding: 16px 18px;
  backdrop-filter: blur(8px);
}

.placeholder-card {
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: rgba(255, 255, 255, 0.9);
}

.status-banner {
  color: #fff;
}

.status-banner.success {
  background: rgba(46, 204, 113, 0.18);
  border: 1px solid rgba(46, 204, 113, 0.35);
}

.status-banner.error {
  background: rgba(231, 76, 60, 0.18);
  border: 1px solid rgba(231, 76, 60, 0.35);
}

.resource-grid {
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  align-content: start;
  padding-right: 4px;
}

.resource-card {
  position: relative;
  padding: 16px 16px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.16);
  backdrop-filter: blur(10px);
  cursor: pointer;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}

.resource-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 14px 32px rgba(0, 0, 0, 0.2);
}

.delete-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 12px;
  background: rgba(255, 107, 107, 0.16);
  color: #ff9a9a;
  border: 1px solid rgba(255, 107, 107, 0.24);
}

.delete-btn:hover {
  background: rgba(255, 107, 107, 0.28);
}

.resource-index {
  display: inline-block;
  margin-bottom: 12px;
  padding: 5px 10px;
  border-radius: 999px;
  font-size: 12px;
  color: #d2ddff;
  background: rgba(102, 126, 234, 0.16);
}

.resource-name {
  margin: 0 0 10px;
  font-size: 18px;
  line-height: 1.4;
}

.resource-url {
  min-height: 44px;
  margin: 0 0 16px;
  color: rgba(255, 255, 255, 0.72);
  font-size: 13px;
  line-height: 1.6;
  word-break: break-all;
}

.resource-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.resource-tag {
  padding: 5px 10px;
  border-radius: 999px;
  font-size: 12px;
  background: rgba(255, 255, 255, 0.12);
  color: #e7efff;
}

.resource-open {
  font-size: 13px;
  color: #8cc6ff;
}

.modal-mask {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(0, 0, 0, 0.45);
}

.modal-card {
  width: 100%;
  max-width: 520px;
  border-radius: 18px;
  background: #fff;
  color: #1f2937;
  overflow: hidden;
  box-shadow: 0 20px 48px rgba(15, 23, 42, 0.3);
}

.modal-header,
.modal-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
}

.modal-header {
  border-bottom: 1px solid #e5e7eb;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #111827;
}

.modal-close {
  padding: 6px 12px;
  border-radius: 8px;
  background: #f3f4f6;
  color: #374151;
}

.modal-body {
  padding: 18px 20px 8px;
  color: #111827;
}

.form-label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #111827;
}

.form-input {
  width: 100%;
  box-sizing: border-box;
  margin-bottom: 16px;
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px solid #d1d5db;
  outline: none;
  font-size: 14px;
  color: #111827;
  background: #ffffff;
}

.form-input::placeholder {
  color: #9ca3af;
}

.form-input:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.14);
}

.form-tip {
  margin: 0 0 10px;
  font-size: 12px;
  color: #6b7280;
}

.modal-footer {
  gap: 12px;
  justify-content: flex-end;
  border-top: 1px solid #e5e7eb;
}

.modal-footer .ghost-btn {
  background: #f3f4f6;
  color: #111827;
  border: 1px solid #d1d5db;
}

@media (max-width: 1024px) {
  .resource-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .resource-grid {
    grid-template-columns: 1fr;
  }
}
</style>
